import React from "react";
import { Collapse, Drawer, Segmented, Select, Switch, Tooltip } from "antd";
import { useLocation } from "react-router-dom";
import { HorizontalSlider } from "components/HorizontalSlider";
import { Ico } from "components/Uikit";
import { IAudioState, ILang } from "providers/AudioProvider/types";
import { IAudioHandles } from "providers/AudioProvider/handleTypes";
import { fontOptions } from "../../../videos/components/PictureModal/constants";
import { VolumeCross } from "components/elements/VolumeCross/VolumeCross";
import { Voicing } from "./components/Voicing";
import { ResyncAudio } from "./components/ResyncAudio";
import { SourceHealthCheck } from "./components/SourceHealthCheck";
import { TranslationPostProcess } from "./components/TranslationPostProcess";
import { TranslateFolderBulk } from "./components/TranslateFolderBulk";
import { DiagnoseButton } from "../DiagnoseButton";
import { FolderPrepareButton, RebuildVoiceoversButton } from "../FolderPrepare";
import { Speed } from "./components/Speed";
import { ClearDB } from "./components/ClearDB";
import { VoiceoverV2Debug } from "./components/VoiceoverV2Debug";
import { ReprocessClip } from "./components/ReprocessClip";
import { ManualOnlySwitch } from "../ManualOnly";
import { SettingRow, SettingsGroup } from "./SettingRow";
import { SETTINGS_ACTIONS } from "./settingsActions";
import { settingsCopy } from "./settingsCopy";
import { setUiLang, useUiLang } from "utils/uiLanguage";
import { PanelScopeSC } from "./styled";

/**
 * The editor's settings panel.
 *
 * What this replaced: one horizontal strip holding some twenty-five controls in mount order —
 * sliders, popovers, icon-only buttons and pipeline actions side by side, with nothing saying
 * which of them changes what you hear, which rewrites the folder's text, and which publishes to
 * production. The strip could be read only by someone who already knew what was on it.
 *
 * So the panel is organised the way the work is: what you HEAR, what you SEE, then the source,
 * the translation, the voicing, and finally the handover — in the order a folder moves through
 * them. Each row says in words what its button does and to what, because «Подготовить озвучку»
 * and «Пересоздать озвучку» are not distinguishable from their labels alone, and one of them
 * throws away every voiceover in the folder. Machinery nobody needs during a normal day is real
 * and kept, but folded away under «Служебное».
 *
 * All of it in either language (settingsCopy + utils/uiLanguage), switched from the header and
 * remembered; and the source-language category names the language that is actually there — a
 * lecture translated FROM English has no Russian in it to head a category with.
 *
 * The buttons themselves were not moved. Each still lives beside the state it acts on and
 * renders into its row through a slot (utils/settingsSlots), so this file decides where things
 * appear and what they are called, and nothing here can break a handover or a publish.
 */

interface ISettingsPanelProps {
    open: boolean;
    onClose: () => void;
    handles: IAudioHandles;
    state: IAudioState;
    view: string[];
    isDebugMode: boolean;
}

export function SettingsPanel({
    open,
    onClose,
    handles,
    state,
    view,
    isDebugMode,
}: ISettingsPanelProps) {
    const uiLang = useUiLang();
    const t = settingsCopy(uiLang, state.originalLang);

    const handleChangeCaptions = (lang: ILang) => (value: number) => {
        handles.changeCaptionSize({ lang, size: value });
    };

    const hasClip = new URLSearchParams(useLocation().search).has("clip");
    const source = String(state.originalLang || "ru").toUpperCase();
    const target = String(state.lang || "").toUpperCase();
    const isTranslation = Boolean(state.lang && state.lang !== state.originalLang);
    // «Без автоправок»: кнопки, которые правят папку сами, выключены в своих компонентах
    // (ManualOnly/AutoEditGate); строка говорит это словами — подсказки на планшете не видно.
    const off = state.manualOnly ? t.autoEditOff : null;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="right"
            width={460}
            title={t.title}
            extra={
                <Tooltip title={t.langSwitchHint}>
                    <Segmented
                        size="small"
                        value={uiLang}
                        onChange={(value) => setUiLang(value as "ru" | "en")}
                        options={[
                            { label: "RU", value: "ru" },
                            { label: "EN", value: "en" },
                        ]}
                    />
                </Tooltip>
            }
            /* No mask: the point of most of these controls is to hear or see the change in the
               editor while you drag them. */
            mask={false}
            styles={{ body: { padding: 0 }, header: { padding: "12px 16px" } }}
        >
            <PanelScopeSC>
                {t.scope(String(state.folder ?? "—"), source, target, isTranslation)}
            </PanelScopeSC>

            {/* Первым: режим решает, что сохраняет каждая правка ниже, — и только у пары. */}
            {state.manualOnlyApplicable && (
                <SettingsGroup title={t.groups.mode} hint={t.groups.modeHint} accent="mode">
                    <SettingRow title={t.rows.manualOnly.title} hint={t.rows.manualOnly.hint}>
                        <ManualOnlySwitch state={state} handles={handles} />
                    </SettingRow>
                </SettingsGroup>
            )}

            <SettingsGroup title={t.groups.playback} accent="playback">
                {view.includes("speed") && (
                    <SettingRow title={t.rows.speed.title} hint={t.rows.speed.hint} wide>
                        <Speed />
                    </SettingRow>
                )}
                <SettingRow title={t.rows.balance.title} hint={t.rows.balance.hint} wide>
                    <VolumeCross
                        setVolumeOriginal={handles.setVolumeOriginal}
                        setVolumeTranslated={handles.setVolumeTranslated}
                        volumeOriginal={state.volumeOriginal}
                        volumeTranslated={state.volumeTranslated}
                    />
                </SettingRow>
                {/* Clicking a voicing used to silence the lecturer unless you knew to hold
                    Ctrl/Cmd — an invisible rule to be rediscovered every session. */}
                <SettingRow title={t.rows.muteOriginal.title} hint={t.rows.muteOriginal.hint}>
                    <Switch
                        checked={state.muteOriginalOnVoicingClick}
                        onChange={(checked) => handles.setMuteOriginalOnVoicingClick(checked)}
                    />
                </SettingRow>
            </SettingsGroup>

            <SettingsGroup title={t.groups.view} accent="view">
                {view.includes("zoomLevel") && (
                    <SettingRow title={t.rows.zoom.title} hint={t.rows.zoom.hint} wide>
                        <Ico style={{ fontSize: "1.6em" }}>zoom_out</Ico>
                        <HorizontalSlider
                            value={state.zoomLevel}
                            min={10}
                            max={90}
                            width={200}
                            onValueChange={(val) => handles.setZoomLevel(val)}
                        />
                        <Ico style={{ fontSize: "1.6em" }}>zoom_in</Ico>
                    </SettingRow>
                )}
                <SettingRow title={t.rows.fontSource(source)}>
                    <Select
                        style={{ width: 92 }}
                        onChange={handleChangeCaptions(state.originalLang)}
                        options={fontOptions}
                        value={state.captionsSize[state.originalLang]}
                    />
                </SettingRow>
                <SettingRow title={t.rows.fontTarget(target)}>
                    <Select
                        style={{ width: 92 }}
                        onChange={handleChangeCaptions(state.lang)}
                        options={fontOptions}
                        value={state.captionsSize[state.lang]}
                    />
                </SettingRow>
                {/* Время реплики считается от начала её папки, а папок в лекции бывает больше
                    десяти. Где это место в целой лекции — приходилось считать в уме. */}
                <SettingRow title={t.rows.globalTime.title} hint={t.rows.globalTime.hint}>
                    <Switch
                        checked={state.showGlobalTime}
                        onChange={(checked) => handles.setShowGlobalTime(checked)}
                    />
                </SettingRow>
                {/* Дорожки нарисованы по вертикали, и звучащее место всё время уходит вниз за
                    край экрана: без этого переводчик подкручивает ленту рукой десятки раз за
                    папку. */}
                <SettingRow title={t.rows.followPlayback.title} hint={t.rows.followPlayback.hint}>
                    <Switch
                        checked={state.followPlayback}
                        onChange={(checked) => handles.setFollowPlayback(checked)}
                    />
                </SettingRow>
                {isDebugMode && (
                    <SettingRow title={t.rows.freeMovement.title} hint={t.rows.freeMovement.hint}>
                        <Switch
                            checked={state.disableMovementRestrictions}
                            onChange={handles.setDisableMovementRestrictions}
                        />
                    </SettingRow>
                )}
            </SettingsGroup>

            <SettingsGroup title={t.groups.source} hint={t.groups.sourceHint} accent="source">
                <SettingRow title={t.rows.fixSource.title} hint={t.rows.fixSource.hint} off={off?.all} wide>
                    <SourceHealthCheck />
                </SettingRow>
                {view.includes("voicingSpacesMinimum") && (
                    <SettingRow title={t.rows.voids.title} hint={t.rows.voids.hint}>
                        <Voicing />
                    </SettingRow>
                )}
                <SettingRow
                    title={t.rows.markSourceDone.title}
                    hint={t.rows.markSourceDone.hint}
                    slot={SETTINGS_ACTIONS.markRuDone}
                />
            </SettingsGroup>

            <SettingsGroup title={t.groups.translation} accent="translation">
                <SettingRow title={t.rows.translateAll.title} hint={t.rows.translateAll.hint} off={off?.all}>
                    <TranslateFolderBulk />
                </SettingRow>
                <SettingRow
                    title={t.rows.recreateFromSource.title}
                    hint={t.rows.recreateFromSource.hint}
                    off={off?.all}
                    slot={SETTINGS_ACTIONS.recreateFromRu}
                />
                <SettingRow
                    title={t.rows.allLanguages.title}
                    hint={t.rows.allLanguages.hint}
                    off={off?.all}
                    danger
                    slot={SETTINGS_ACTIONS.allLanguages}
                />
                <SettingRow
                    title={t.rows.postProcess.title}
                    hint={t.rows.postProcess.hint}
                    off={off?.applyOnly}
                    wide
                >
                    <TranslationPostProcess />
                </SettingRow>
            </SettingsGroup>

            <SettingsGroup title={t.groups.voicing} accent="voicing">
                <SettingRow
                    title={t.rows.prepareVoicing.title}
                    hint={t.rows.prepareVoicing.hint}
                    off={off?.all}
                >
                    <FolderPrepareButton />
                </SettingRow>
                <SettingRow
                    title={t.rows.checkedSynthesis.title}
                    hint={t.rows.checkedSynthesis.hint}
                    off={off?.folderRuns}
                    wide
                >
                    <VoiceoverV2Debug />
                </SettingRow>
                <SettingRow title={t.rows.resync.title} hint={t.rows.resync.hint} off={off?.all}>
                    <ResyncAudio />
                </SettingRow>
                <SettingRow
                    title={t.rows.rebuildVoicing.title}
                    hint={t.rows.rebuildVoicing.hint}
                    off={off?.all}
                    danger
                >
                    <RebuildVoiceoversButton />
                </SettingRow>
            </SettingsGroup>

            <SettingsGroup title={t.groups.publish} hint={t.groups.publishHint} accent="publish">
                <SettingRow
                    title={t.rows.handIn.title}
                    hint={t.rows.handIn.hint}
                    slot={SETTINGS_ACTIONS.handIn}
                />
                <SettingRow
                    title={t.rows.updateHls.title}
                    hint={t.rows.updateHls.hint}
                    slot={SETTINGS_ACTIONS.updateHls}
                />
                {/* ReprocessClip рисует себя только при ?clip — строка спрашивает у адреса то
                    же самое, чтобы не остаться подписью без кнопки. */}
                <SettingRow
                    title={t.rows.reprocessClip.title}
                    hint={t.rows.reprocessClip.hint}
                    danger
                    available={hasClip}
                >
                    <ReprocessClip />
                </SettingRow>
                <SettingRow
                    title={t.rows.republishState.title}
                    hint={t.rows.republishState.hint}
                    slot={SETTINGS_ACTIONS.republishBadge}
                />
                <SettingRow
                    title={t.rows.managementBoard.title}
                    hint={t.rows.managementBoard.hint}
                    slot={SETTINGS_ACTIONS.managementBoard}
                />
            </SettingsGroup>

            <Collapse
                ghost
                items={[
                    {
                        key: "service",
                        label: t.groups.service,
                        children: (
                            <SettingsGroup accent="service">
                                <SettingRow
                                    title={t.rows.diagnose.title}
                                    hint={t.rows.diagnose.hint}
                                    off={off?.applyOnly}
                                >
                                    <DiagnoseButton />
                                </SettingRow>
                                <SettingRow
                                    title={t.rows.checkData.title}
                                    hint={t.rows.checkData.hint}
                                    slot={SETTINGS_ACTIONS.checkData}
                                />
                                <SettingRow
                                    title={t.rows.clearCache.title}
                                    hint={t.rows.clearCache.hint}
                                >
                                    <ClearDB />
                                </SettingRow>
                            </SettingsGroup>
                        ),
                    },
                ]}
            />

            <div style={{ padding: "4px 16px 20px", fontSize: 11, color: "#bfbfbf" }}>
                {isDebugMode ? (uiLang === "en" ? "debug mode on" : "debug-режим включён") : ""}
            </div>
        </Drawer>
    );
}
